import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { capacitorService } from '../../../../application/CapacitorService';
import { routeService } from '../../../../application/RouteService';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { FREE_ROUTES_LIMIT } from '../../components/upgradeSubscription/UpgradeSubscriptionComponent';

interface UseSearchDailyRoutesProps {
  day: Date;
  enabled: boolean;
}
type UseSearchDailyRoutesResponseProps = ServiceResponse<Function, boolean>;

export const useSearchDailyRoutes = ({
  day,
  enabled,
}: UseSearchDailyRoutesProps): UseSearchDailyRoutesResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const startDay = new Date(day);
  startDay.setHours(0, 0, 0, 0);

  const endDay = new Date(startDay);
  endDay.setHours(24, 0, 0, 0);

  const { isInitialLoading, isSuccess, isError, refetch, isRefetching, data } = useQuery(
    [`daily_route`, startDay.toISOString()],
    () =>
      routeService.searchRoutes({
        sort_by: `plan_execution_date`,
        sort_direction: `desc`,
        limit: 1,
        predicates: [
          SearchPredicate.create({
            field: `plan_execution_date`,
            operator: `gte`,
            type: `date`,
            value: startDay,
          }),
          SearchPredicate.create({
            field: `plan_execution_date`,
            operator: `lte`,
            type: `date`,
            value: endDay,
          }),
        ],
      }),
    {
      retry: false,
      enabled,
      refetchOnWindowFocus: capacitorService.isNative(),
      onError: (err: any) => {
        console.info(`Retrieve Vehicle Previous Routes Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: err.response?.data?.messageId ?? `network_error`,
        });
      },
    }
  );
  return {
    error: isError,
    success: isSuccess,
    isLoading: isRefetching || isInitialLoading,
    data: (data?.total ?? 0) >= FREE_ROUTES_LIMIT,
    run: refetch,
  };
};
